import React, { useState, useEffect, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { 
  FaRocket, 
  FaChartLine, 
  FaUsers, 
  FaHandshake, 
  FaMoneyBillWave, 
  FaCalendarAlt,
  FaCheck,
  FaArrowRight,
  FaUpload,
  FaSpinner,
  FaCheckCircle
} from 'react-icons/fa';
import Footer from '../components/Footer';

function AccelerationProgram() {
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [programData, setProgramData] = useState(null);
  const [error, setError] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [fileError, setFileError] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // reCAPTCHA ref
  const recaptchaRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    // Section 1: Basic Details
    founderName: '',
    email: '',
    mobileNumber: '',
    cityState: '',
    
    // Section 2: Business Information
    companyName: '',
    businessStage: '',
    industrySector: '',
    yearOfIncorporation: '',
    registrationStatus: '',
    
    // Section 3: Team & Operations
    numberOfFounders: '',
    teamSize: '',
    monthlyRevenue: '',
    
    // Section 4: Growth & Fundraising
    raisedFundsEarlier: '',
    fundingRequirement: '',
    primaryGoals: [],
    
    // Section 5: Short Inputs
    businessDescription: '',
    growthChallenge: '',
    
    // Section 6: Declarations
    agreeToTerms: false,
    consentToCommunication: false
  });

  // API URLs
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const API_PROGRAM = `${API_BASE_URL}/UIacceleration-program`;
  const API_MEDIA = `${API_BASE_URL}/UImedia/upload`;
  const API_APPLICATIONS = `${API_BASE_URL}/acceleration-applications`;

  // Fetch program data from backend
  useEffect(() => {
    const fetchProgramData = async () => {
      try {
        setLoading(true);
        const response = await fetch(API_PROGRAM);
        if (!response.ok) {
          throw new Error('Failed to fetch program data');
        }
        
        const result = await response.json();
        
        if (result.success && result.data) {
          setProgramData(result.data);
        } else {
          throw new Error('Invalid data format');
        }
      } catch (err) {
        console.error('Error fetching program data:', err);
        setError('Failed to load program information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProgramData();
  }, [API_PROGRAM]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'primaryGoals') {
        const updatedGoals = formData.primaryGoals.includes(value)
          ? formData.primaryGoals.filter(goal => goal !== value)
          : [...formData.primaryGoals, value];
        setFormData({ ...formData, primaryGoals: updatedGoals });
      } else {
        setFormData({ ...formData, [name]: checked });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // File validation
    const allowedTypes = ['application/pdf', 
                         'application/vnd.ms-powerpoint', 
                         'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                         'application/msword',
                         'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    if (!allowedTypes.includes(file.type)) {
      setFileError('Only PDF, PPT, DOC files are allowed');
      return;
    }
    
    if (file.size > maxSize) {
      setFileError('File size should be less than 10MB');
      return;
    }
    
    setSelectedFile(file);
    setFileError('');
  };

  // Upload file to media endpoint and get media ID - FIXED VERSION
  const uploadFileToServer = async (file) => {
    if (!file) return null;

    try {
      setUploadingFile(true);
      const formDataToSend = new FormData();
      formDataToSend.append('file', file);
      formDataToSend.append('name', file.name);
      formDataToSend.append('type', file.type);

      const response = await fetch(API_MEDIA, {
        method: 'POST',
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      
      // Your backend returns direct object, not {success: true, data: {...}}
      // Get id from response
      const mediaId = result?.id || result?._id;
      
      if (!mediaId) {
        console.error("Media uploaded but ID missing:", result);
        // Don't throw error, just return null (file is optional)
        return null;
      }

      return mediaId;
    } catch (error) {
      console.error('Error uploading file:', error);
      // Don't show error for optional file upload
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  // Handle reCAPTCHA change
  const handleCaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  // Handle reCAPTCHA expiration
  const handleCaptchaExpired = () => {
    setRecaptchaToken(null);
  };

  // Handle form submission - FIXED VERSION
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.agreeToTerms) {
      alert('Please agree to the program terms and conditions');
      return;
    }
    
    // reCAPTCHA validation
    if (!recaptchaToken) {
      alert('Please complete the CAPTCHA verification');
      return;
    }

    setIsSubmitting(true);
    setFileError('');

    try {
      // Step 1: Upload file if exists (file is optional, so don't fail if upload fails)
      let mediaId = null;
      if (selectedFile) {
        const uploadedId = await uploadFileToServer(selectedFile);
        mediaId = uploadedId; // Can be null if upload fails, that's OK
      }

      // Step 2: Prepare application data
      const applicationData = {
        // Section 1: Basic Details
        founderName: formData.founderName,
        email: formData.email,
        mobileNumber: formData.mobileNumber,
        cityState: formData.cityState,
        
        // Section 2: Business Information
        companyName: formData.companyName,
        businessStage: formData.businessStage,
        industrySector: formData.industrySector,
        yearOfIncorporation: formData.yearOfIncorporation || "",
        registrationStatus: formData.registrationStatus || "",
        
        // Section 3: Team & Operations
        numberOfFounders: formData.numberOfFounders || "",
        teamSize: formData.teamSize || "",
        monthlyRevenue: formData.monthlyRevenue || "",
        
        // Section 4: Growth & Fundraising
        raisedFundsEarlier: formData.raisedFundsEarlier || "",
        fundingRequirement: formData.fundingRequirement || "",
        primaryGoals: formData.primaryGoals,
        
        // Section 5: Short Inputs
        businessDescription: formData.businessDescription || "",
        growthChallenge: formData.growthChallenge || "",
        
        // Pitchdeck media reference - can be null or undefined
        pitchdeck: mediaId,
        
        // Section 6: Declarations
        agreeToTerms: formData.agreeToTerms,
        consentToCommunication: formData.consentToCommunication || false,
        
        // reCAPTCHA token
        recaptchaToken: recaptchaToken
      };

      // Step 3: Submit to backend endpoint
      const response = await fetch(API_APPLICATIONS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(applicationData),
      });

      const result = await response.json();
      
      if (!response.ok) {
        let errorMessage = result.message || 'Failed to submit application';
        
        if (result.error) {
          errorMessage = result.error;
        }
        if (result.errors) {
          errorMessage = Array.isArray(result.errors) 
            ? result.errors.join(', ') 
            : JSON.stringify(result.errors);
        }
        
        throw new Error(errorMessage);
      }
      
      // Success - check for different possible response formats
      if (response.ok) {
        // Show success popup
        setShowSuccessPopup(true);
        
        // Reset everything
        resetFormAndRecaptcha();
      } else {
        throw new Error(result.message || 'Application submission failed');
      }
      
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      
      // Reset reCAPTCHA on error
      resetRecaptcha();
      
      let userMessage = error.message;
      
      if (error.message.includes('reCAPTCHA')) {
        userMessage = 'reCAPTCHA verification failed. Please try again.';
      } else if (error.message.includes('validation')) {
        userMessage = 'Please check all required fields and try again.';
      } else if (error.message.includes('network')) {
        userMessage = 'Network error. Please check your connection.';
      }
      
      alert(`Error: ${userMessage}`);
      
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to reset form
  const resetFormAndRecaptcha = () => {
    // Reset reCAPTCHA
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
    setRecaptchaToken(null);
    
    // Reset form state
    setFormData({
      founderName: '',
      email: '',
      mobileNumber: '',
      cityState: '',
      companyName: '',
      businessStage: '',
      industrySector: '',
      yearOfIncorporation: '',
      registrationStatus: '',
      numberOfFounders: '',
      teamSize: '',
      monthlyRevenue: '',
      raisedFundsEarlier: '',
      fundingRequirement: '',
      primaryGoals: [],
      businessDescription: '',
      growthChallenge: '',
      agreeToTerms: false,
      consentToCommunication: false
    });
    
    // Reset file
    setSelectedFile(null);
    setFileError('');
    
    // Reset file input field
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = '';
  };

  // Helper function to reset recaptcha only
  const resetRecaptcha = () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
    setRecaptchaToken(null);
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-blue-500 text-4xl mx-auto mb-4" />
          <p className="text-gray-300">Loading program information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaSpinner className="animate-spin text-red-500 text-3xl" />
          </div>
          <p className="text-red-300 text-xl mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold py-2 px-6 rounded-full hover:from-blue-600 hover:to-purple-600 transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!programData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white">
      {/* Hero Section */}
      <section className="relative py-20 px-4">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 via-purple-900/20 to-indigo-900/20" />
        <div className="relative max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            {programData.hero.title}
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-8">
            {programData.hero.description}
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {programData.hero.tags && programData.hero.tags.map((tag, index) => (
              <span 
                key={index}
                className={`bg-${tag.color}-500/20 border border-${tag.color}-500/30 px-4 py-2 rounded-full text-${tag.color}-300`}
              >
                {tag.text}
              </span>
            ))}
          </div>
          
          <button
            onClick={() => document.getElementById('registration-form').scrollIntoView({ behavior: 'smooth' })}
            className="bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold py-4 px-8 rounded-full text-lg hover:from-blue-600 hover:to-purple-600 transition-all transform hover:scale-105"
          >
            <FaRocket className="inline mr-2" />
            Apply for Acceleration Program
          </button>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 py-12">
        {/* Program Overview */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-blue-500/20">
            <h2 className="text-3xl font-bold mb-6 text-blue-300">
              {programData.overview.title}
            </h2>
            <p className="text-lg text-gray-300 mb-8">
              {programData.overview.description}
            </p>
            
            <div className="border-t border-b border-blue-500/20 py-6 my-6">
              <h3 className="text-xl font-bold mb-4 text-blue-400">🔹 Program Snapshot</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {programData.overview.snapshot && programData.overview.snapshot.map((item, index) => (
                  <div key={index} className="bg-gray-800/30 p-4 rounded-lg">
                    <p className="text-sm text-gray-400">{item.label}</p>
                    <p className="text-lg font-bold text-white">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Key Benefits */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-10 text-center text-blue-300">
            🔹 What You Get (Key Benefits)
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {programData.benefits && programData.benefits.map((benefit) => (
              <div 
                key={benefit.id}
                className={`bg-gradient-to-br from-gray-800/50 to-gray-900/50 p-6 rounded-xl border border-${benefit.color}-500/20 hover:border-${benefit.color}-500/40 transition-all relative`}
              >
                <div className={`absolute -top-3 -left-3 w-10 h-10 bg-${benefit.color}-500 rounded-full flex items-center justify-center text-white font-bold text-lg`}>
                  {benefit.id}
                </div>
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-white">{benefit.title}</h3>
                </div>
                <p className="text-gray-300">{benefit.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Funding Support Structure */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 backdrop-blur-sm rounded-2xl p-8 border border-green-500/20">
            <h2 className="text-3xl font-bold mb-6 text-green-300">
              {programData.fundingSupport.title}
            </h2>
            <p className="text-lg text-gray-300 mb-6">
              {programData.fundingSupport.description}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {programData.fundingSupport.tiers && programData.fundingSupport.tiers.map((tier, index) => (
                <div key={index} className="bg-gray-800/30 p-6 rounded-xl text-center">
                  <p className="text-lg text-gray-300 mb-2">{tier.range}</p>
                  <p className="text-3xl font-bold text-green-400">{tier.fee}</p>
                </div>
              ))}
            </div>
            
            <p className="text-center text-gray-400 italic mt-6">
              {programData.fundingSupport.note}
            </p>
          </div>
        </section>

        {/* Program Journey */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-10 text-center text-purple-300">
            🔹 Program Journey (Timeline View)
          </h2>
          
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 h-full w-1 bg-gradient-to-b from-blue-500 to-purple-500"></div>
            
            <div className="space-y-12">
              {programData.journey && programData.journey.map((phase, index) => (
                <div 
                  key={index}
                  className={`relative ${index % 2 === 0 ? 'md:pr-1/2 md:pl-12' : 'md:pl-1/2 md:pr-12'}`}
                >
                  <div className="absolute left-0 md:left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-blue-500 rounded-full border-4 border-gray-900"></div>
                  <div className="ml-12 md:ml-0 bg-gradient-to-br from-gray-800/50 to-gray-900/50 p-6 rounded-xl border border-purple-500/20">
                    <div className="text-purple-400 font-bold mb-2">{phase.week}</div>
                    <h3 className="text-xl font-bold text-white mb-2">{phase.title}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Eligibility Criteria */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-blue-900/20 to-indigo-900/20 backdrop-blur-sm rounded-2xl p-8 border border-blue-500/20">
            <h2 className="text-3xl font-bold mb-6 text-blue-300">
              {programData.eligibility.title}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {programData.eligibility.criteria && programData.eligibility.criteria.map((criterion, index) => (
                <div key={index} className="flex items-start gap-3">
                  <FaCheck className="text-green-400 mt-1 flex-shrink-0" />
                  <p className="text-gray-300">{criterion}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Application Process */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-10 text-center text-white">
            🔹 Application Process
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {programData.applicationProcess && programData.applicationProcess.map((process, index) => (
              <div key={index} className="relative">
                <div className={`bg-gradient-to-br from-gray-800/50 to-gray-900/50 p-6 rounded-xl border border-${process.color}-500/20`}>
                  <div className={`text-3xl font-bold text-${process.color}-500 mb-4`}>{process.step}</div>
                  <p className="text-lg text-gray-300">{process.text}</p>
                </div>
                {index < programData.applicationProcess.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2">
                    <FaArrowRight className={`text-${process.color}-500 text-2xl`} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="mb-16 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">
            {programData.finalCta.title}
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            {programData.finalCta.description}
          </p>
          
          <button
            onClick={() => document.getElementById('registration-form').scrollIntoView({ behavior: 'smooth' })}
            className="bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold py-4 px-12 rounded-full text-xl hover:from-blue-600 hover:to-purple-600 transition-all transform hover:scale-105 flex items-center gap-2 mx-auto"
          >
            <FaRocket />
            Apply Now
          </button>
        </section>

        {/* Registration Form (Dynamic/Interactive Part) */}
        <section id="registration-form" className="mb-20">
          <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl p-8 border border-blue-500/30">
            <h2 className="text-3xl font-bold mb-2 text-center text-blue-300">
              REGISTRATION FORM
            </h2>
            <p className="text-center text-gray-300 mb-8">
              Complete all sections to apply for the Acceleration Program
            </p>

            <form onSubmit={handleSubmit} className="space-y-12">
              {/* Section 1: Basic Details */}
              <div className="bg-gray-800/30 p-6 rounded-xl border border-blue-500/10">
                <h3 className="text-xl font-bold mb-6 pb-3 border-b border-blue-500/30 text-blue-400 flex items-center gap-2">
                  <span className="bg-blue-500/20 px-3 py-1 rounded-full text-sm">Section 1</span>
                  Basic Details
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">
                      Founder / Applicant Name *
                    </label>
                    <input
                      type="text"
                      name="founderName"
                      value={formData.founderName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-white outline-none transition"
                      placeholder="Enter your full name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-white outline-none transition"
                      placeholder="Enter your email address"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">
                      Mobile Number *
                    </label>
                    <input
                      type="tel"
                      name="mobileNumber"
                      value={formData.mobileNumber}
                      onChange={handleInputChange}
                      required
                      pattern="[0-9]{10}"
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-white outline-none transition"
                      placeholder="Enter 10-digit mobile number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">
                      City & State *
                    </label>
                    <input
                      type="text"
                      name="cityState"
                      value={formData.cityState}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-white outline-none transition"
                      placeholder="Enter your city and state"
                    />
                  </div>
                </div>
              </div>

              {/* Section 2: Business Information */}
              <div className="bg-gray-800/30 p-6 rounded-xl border border-purple-500/10">
                <h3 className="text-xl font-bold mb-6 pb-3 border-b border-purple-500/30 text-purple-400 flex items-center gap-2">
                  <span className="bg-purple-500/20 px-3 py-1 rounded-full text-sm">Section 2</span>
                  Business Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">
                      Company / Startup Name *
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-white outline-none transition"
                      placeholder="Enter company name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">
                      Business Stage *
                    </label>
                    <select
                      name="businessStage"
                      value={formData.businessStage}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-white outline-none transition"
                    >
                      <option value="">Select Stage</option>
                      {programData.businessStages && programData.businessStages.map((stage, index) => (
                        <option key={index} value={stage} className="bg-gray-800">
                          {stage}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">
                      Industry / Sector *
                    </label>
                    <input
                      type="text"
                      name="industrySector"
                      value={formData.industrySector}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-white outline-none transition"
                      placeholder="Enter your industry/sector"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">
                      Year of Incorporation
                    </label>
                    <input
                      type="number"
                      name="yearOfIncorporation"
                      value={formData.yearOfIncorporation}
                      onChange={handleInputChange}
                      min="1900"
                      max="2024"
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-white outline-none transition"
                      placeholder="YYYY"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">
                      MSME / Startup Registration
                    </label>
                    <select
                      name="registrationStatus"
                      value={formData.registrationStatus}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-white outline-none transition"
                    >
                      <option value="">Select Option</option>
                      <option value="Yes" className="bg-gray-800">Yes</option>
                      <option value="No" className="bg-gray-800">No</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Section 3: Team & Operations */}
              <div className="bg-gray-800/30 p-6 rounded-xl border border-green-500/10">
                <h3 className="text-xl font-bold mb-6 pb-3 border-b border-green-500/30 text-green-400 flex items-center gap-2">
                  <span className="bg-green-500/20 px-3 py-1 rounded-full text-sm">Section 3</span>
                  Team & Operations
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">
                      Number of Founders
                    </label>
                    <input
                      type="number"
                      name="numberOfFounders"
                      value={formData.numberOfFounders}
                      onChange={handleInputChange}
                      min="1"
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-white outline-none transition"
                      placeholder="Enter number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">
                      Total Team Size
                    </label>
                    <input
                      type="number"
                      name="teamSize"
                      value={formData.teamSize}
                      onChange={handleInputChange}
                      min="1"
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-white outline-none transition"
                      placeholder="Enter team size"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">
                      Current Monthly Revenue
                    </label>
                    <select
                      name="monthlyRevenue"
                      value={formData.monthlyRevenue}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-white outline-none transition"
                    >
                      <option value="">Select Range</option>
                      {programData.revenueRanges && programData.revenueRanges.map((range, index) => (
                        <option key={index} value={range} className="bg-gray-800">
                          {range}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Section 4: Growth & Fundraising */}
              <div className="bg-gray-800/30 p-6 rounded-xl border border-yellow-500/10">
                <h3 className="text-xl font-bold mb-6 pb-3 border-b border-yellow-500/30 text-yellow-400 flex items-center gap-2">
                  <span className="bg-yellow-500/20 px-3 py-1 rounded-full text-sm">Section 4</span>
                  Growth & Fundraising
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">
                      Have you raised funds earlier?
                    </label>
                    <select
                      name="raisedFundsEarlier"
                      value={formData.raisedFundsEarlier}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 text-white outline-none transition"
                    >
                      <option value="">Select Option</option>
                      <option value="Yes" className="bg-gray-800">Yes</option>
                      <option value="No" className="bg-gray-800">No</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">
                      Funding Requirement
                    </label>
                    <select
                      name="fundingRequirement"
                      value={formData.fundingRequirement}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 text-white outline-none transition"
                    >
                      <option value="">Select Range</option>
                      {programData.fundingRanges && programData.fundingRanges.map((range, index) => (
                        <option key={index} value={range} className="bg-gray-800">
                          {range}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-gray-300 mb-2 font-medium">
                      Primary Goal from Program (checkbox – multiple)
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      {programData.goalOptions && programData.goalOptions.map((goal, index) => (
                        <label
                          key={index}
                          className={`flex items-center gap-3 p-4 rounded-lg cursor-pointer transition-colors ${formData.primaryGoals.includes(goal) ? 'bg-yellow-500/20 border border-yellow-500/30' : 'bg-gray-700/30 border border-gray-600 hover:bg-gray-700/50'}`}
                        >
                          <input
                            type="checkbox"
                            name="primaryGoals"
                            value={goal}
                            checked={formData.primaryGoals.includes(goal)}
                            onChange={handleInputChange}
                            className="text-yellow-500 focus:ring-yellow-500"
                          />
                          <span className="text-gray-300">{goal}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 5: Short Inputs */}
              <div className="bg-gray-800/30 p-6 rounded-xl border border-pink-500/10">
                <h3 className="text-xl font-bold mb-6 pb-3 border-b border-pink-500/30 text-pink-400 flex items-center gap-2">
                  <span className="bg-pink-500/20 px-3 py-1 rounded-full text-sm">Section 5</span>
                  Short Inputs
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">
                      One-line business description (textarea – 200 chars)
                    </label>
                    <textarea
                      name="businessDescription"
                      value={formData.businessDescription}
                      onChange={handleInputChange}
                      rows="2"
                      maxLength="200"
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 text-white outline-none transition resize-none"
                      placeholder="Describe your business in one line..."
                    />
                    <div className="text-right text-sm text-gray-400 mt-1">
                      {formData.businessDescription.length}/200 characters
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">
                      Key growth challenge (textarea)
                    </label>
                    <textarea
                      name="growthChallenge"
                      value={formData.growthChallenge}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 text-white outline-none transition resize-none"
                      placeholder="What is your biggest growth challenge?"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">
                      Upload Pitchdeck (Optional)
                    </label>
                    <div className="mt-2">
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer bg-gray-800/30 hover:bg-gray-800/50 transition-colors">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <FaUpload className="w-8 h-8 mb-4 text-gray-400" />
                          <p className="mb-2 text-sm text-gray-400">
                            <span className="font-semibold">Click to upload</span>
                          </p>
                          <p className="text-xs text-gray-500">PDF / PPT / DOC (Max 10MB)</p>
                        </div>
                        <input
                          type="file"
                          onChange={handleFileChange}
                          className="hidden"
                          accept=".pdf,.ppt,.pptx,.doc,.docx"
                        />
                      </label>
                      
                      {/* Show selected file info */}
                      {selectedFile && (
                        <div className="mt-2 p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-green-400 text-sm font-medium">
                                {selectedFile.name}
                              </p>
                              <p className="text-green-400/70 text-xs">
                                {formatFileSize(selectedFile.size)}
                              </p>
                            </div>
                            {uploadingFile && (
                              <FaSpinner className="animate-spin text-green-400" />
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Show file error */}
                      {fileError && (
                        <div className="mt-2 p-2 bg-red-500/10 rounded border border-red-500/30">
                          <p className="text-red-400 text-sm">{fileError}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 6: Declarations */}
              <div className="bg-gray-800/30 p-6 rounded-xl border border-red-500/10">
                <h3 className="text-xl font-bold mb-6 pb-3 border-b border-red-500/30 text-red-400 flex items-center gap-2">
                  <span className="bg-red-500/20 px-3 py-1 rounded-full text-sm">Section 6</span>
                  Declarations
                </h3>
                
                <div className="space-y-4">
                  <label className="flex items-start gap-3 p-4 bg-gray-800/30 rounded-lg border border-gray-600">
                    <input
                      type="checkbox"
                      name="agreeToTerms"
                      checked={formData.agreeToTerms}
                      onChange={handleInputChange}
                      required
                      className="mt-1 text-red-500 focus:ring-red-500"
                    />
                    <div>
                      <span className="block text-gray-300 font-medium mb-1">
                        Agreement to program fee & success-based stake (checkbox – mandatory)
                      </span>
                      <p className="text-gray-400 text-sm">
                        I agree to the program fee of ₹25,000 and understand the success-based fee structure for funding raised through MASA's support.
                      </p>
                    </div>
                  </label>
                  
                  <label className="flex items-start gap-3 p-4 bg-gray-800/30 rounded-lg border border-gray-600">
                    <input
                      type="checkbox"
                      name="consentToCommunication"
                      checked={formData.consentToCommunication}
                      onChange={handleInputChange}
                      className="mt-1 text-red-500 focus:ring-red-500"
                    />
                    <div>
                      <span className="block text-gray-300 font-medium mb-1">
                        Consent to receive communication from MASA (checkbox)
                      </span>
                      <p className="text-gray-400 text-sm">
                        I consent to receive updates, newsletters, and communication regarding the acceleration program and other MASA initiatives.
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* reCAPTCHA Section */}
              <div className="bg-gray-800/30 p-6 rounded-xl border border-orange-500/10">
                <h3 className="text-xl font-bold mb-6 pb-3 border-b border-orange-500/30 text-orange-400 flex items-center gap-2">
                  <span className="bg-orange-500/20 px-3 py-1 rounded-full text-sm">Security Check</span>
                  Verify you're human
                </h3>
                
                <div className="space-y-4">
                  <div className="flex flex-col items-center">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                      onChange={handleCaptchaChange}
                      onExpired={handleCaptchaExpired}
                      theme="dark"
                      className="mb-4"
                    />
                    
                    <p className="text-sm text-gray-400 mb-4 text-center">
                      This site is protected by reCAPTCHA and the Google 
                      <a href="https://policies.google.com/privacy" className="text-blue-400 hover:text-blue-300 ml-1" target="_blank" rel="noopener noreferrer">
                        Privacy Policy
                      </a> and 
                      <a href="https://policies.google.com/terms" className="text-blue-400 hover:text-blue-300 ml-1" target="_blank" rel="noopener noreferrer">
                        Terms of Service
                      </a> apply.
                    </p>
                    
                    {recaptchaToken && (
                      <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30 mb-4">
                        <div className="flex items-center gap-2 text-green-400">
                          <FaCheckCircle />
                          <span className="text-sm">reCAPTCHA verification completed</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting || uploadingFile || !formData.agreeToTerms}
                  className={`w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold py-4 px-6 rounded-lg transition-all transform focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 ${isSubmitting || !formData.agreeToTerms || uploadingFile ? 'opacity-50 cursor-not-allowed' : 'hover:from-blue-600 hover:to-purple-600 hover:scale-105'}`}
                >
                  {isSubmitting || uploadingFile ? (
                    <span className="flex items-center justify-center gap-2">
                      <FaSpinner className="animate-spin" />
                      {uploadingFile ? 'UPLOADING FILE...' : 'SUBMITTING APPLICATION...'}
                    </span>
                  ) : (
                    'SUBMIT APPLICATION'
                  )}
                </button>
                
                <p className="text-gray-400 text-sm mt-4">
                  By submitting, you agree to our Terms and Conditions
                </p>
              </div>
            </form>
          </div>
        </section>
      </main>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-green-500/30 rounded-2xl max-w-md w-full p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaCheckCircle className="text-2xl text-white" />
              </div>
              
              <h3 className="text-2xl font-bold text-green-300 mb-4">
                Application Submitted Successfully!
              </h3>
              
              <p className="text-gray-300 mb-6">
                Thank you for applying to the MASA Acceleration Program. Our team will review your application and contact you within 5-7 business days.
              </p>
              
              <button
                onClick={() => {
                  setShowSuccessPopup(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold py-3 px-6 rounded-lg w-full hover:from-green-600 hover:to-emerald-600 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default AccelerationProgram;